/**
 * Feature Flag Configuration
 * Defines preview feature tiers (alpha, beta, prototype) and their associated routes
 *
 * Feature tiers represent development stages:
 * - prototype: Experimental features in early development (may be incomplete)
 * - beta: Features in testing phase (mostly functional)
 * - alpha: Early access features (functional but may lack polish)
 *
 * Feature flag states:
 * - disabled: Feature is blocked/hidden from user
 * - warning: Feature is enabled but shows warning badges (user acknowledges experimental status)
 * - enabled: Feature is fully enabled without warnings
 */

/**
 * Feature tier identifiers
 */
export type FeatureTier = 'alpha' | 'beta' | 'prototype';

/**
 * Feature flag state values
 * - 'disabled': Feature is blocked/hidden
 * - 'warning': Feature is enabled but shows warning badges and tooltips
 * - 'enabled': Feature is fully enabled without warnings
 */
export type FeatureFlagState = 'disabled' | 'warning' | 'enabled';

/**
 * Feature flag keys used in the featureFlags state
 * Maps to tier names for easy identification
 */
export const FEATURE_FLAG_KEYS = {
  prototype: 'preview_prototype',
  beta: 'preview_beta',
  alpha: 'preview_alpha',
} as const;

export type FeatureFlagKey = (typeof FEATURE_FLAG_KEYS)[FeatureTier];

/**
 * Feature tier configuration with metadata and associated routes
 */
export interface FeatureTierConfig {
  /** Display name for the tier */
  name: string;
  /** Feature flag key used in state */
  flagKey: FeatureFlagKey;
  /** Description shown in UI */
  description: string;
  /** Routes gated by this tier */
  routes: string[];
  /** In-call features gated by this tier (e.g., tabs in RightPanel) */
  inCallFeatures?: string[];
  /** Example pages for display */
  examples: string[];
  /** Tier badge color (Tailwind class) */
  badgeColor: string;
  /** Sort order in UI (lower = first) */
  order: number;
}

/**
 * Feature tier definitions
 * Routes listed here will be:
 * 1. Hidden from navigation when the tier is disabled
 * 2. Show a "feature not enabled" gate when accessed directly via URL
 */
export const FEATURE_TIERS: Record<FeatureTier, FeatureTierConfig> = {
  prototype: {
    name: 'Prototype Features',
    flagKey: FEATURE_FLAG_KEYS.prototype,
    description: 'Experimental features in early development',
    // '/dashboard/analytics' was removed from this tier when the page became a
    // 308 into /adminpanel/call-metrics: it is no longer a prototype feature,
    // it is a redirect to a shipped one. Leaving it gated meant a user with the
    // prototype flag off got "feature not enabled" instead of being forwarded
    // to the canonical page they already have access to.
    routes: ['/ai-demo', '/search/advanced'],
    inCallFeatures: ['search'],
    examples: ['AI Demo', 'In-Call Search'],
    badgeColor: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    order: 1,
  },
  beta: {
    name: 'Beta Features',
    flagKey: FEATURE_FLAG_KEYS.beta,
    description: 'Features in testing phase',
    routes: ['/developer/billing', '/api-docs', '/developers'],
    examples: ['Developer Billing', 'API Docs'],
    badgeColor: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
    order: 2,
  },
  alpha: {
    name: 'Alpha Features',
    flagKey: FEATURE_FLAG_KEYS.alpha,
    description: 'Early access features',
    routes: ['/privacy', '/terms', '/feedback'],
    examples: ['Privacy', 'Terms', 'Feedback'],
    badgeColor: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    order: 3,
  },
} as const;

/**
 * All feature flag keys for easy iteration
 */
export const ALL_FEATURE_FLAGS: FeatureFlagKey[] = Object.values(FEATURE_FLAG_KEYS);

/**
 * Default feature flag values (prototype enabled by default)
 */
export const DEFAULT_FEATURE_FLAGS: Record<FeatureFlagKey, FeatureFlagState> = {
  [FEATURE_FLAG_KEYS.prototype]: 'enabled',
  [FEATURE_FLAG_KEYS.beta]: 'disabled',
  [FEATURE_FLAG_KEYS.alpha]: 'disabled',
};

/**
 * Check if a feature flag state means the feature is accessible (warning or enabled)
 */
export function isFeatureAccessible(state: FeatureFlagState | boolean | undefined): boolean {
  // Handle legacy boolean values for backward compatibility
  if (typeof state === 'boolean') {
    return state;
  }
  return state === 'warning' || state === 'enabled';
}

/**
 * Check if a feature flag state should show warning badges
 */
export function shouldShowWarningBadge(state: FeatureFlagState | boolean | undefined): boolean {
  return state === 'warning';
}

/**
 * Normalize legacy boolean values to new tri-state format
 */
export function normalizeFeatureFlagState(value: boolean | FeatureFlagState | undefined): FeatureFlagState {
  if (value === true) return 'enabled';
  if (value === false || value === undefined) return 'disabled';
  return value;
}

/**
 * LocalStorage key for persisting feature flags
 */
export const FEATURE_FLAGS_STORAGE_KEY = 'neutralaiz_feature_flags';

/**
 * Get feature tier for a given route path
 * @param pathname - The route path to check
 * @returns The feature tier or null if not gated
 */
export function getFeatureTierForRoute(pathname: string): FeatureTier | null {
  // Normalize path (remove trailing slash)
  const normalizedPath = pathname === '/' ? '/' : pathname.replace(/\/$/, '');

  for (const [tier, config] of Object.entries(FEATURE_TIERS)) {
    for (const route of config.routes) {
      // Exact match
      if (normalizedPath === route) {
        return tier as FeatureTier;
      }
      // Prefix match for nested routes (e.g., /developers matches /developers/*)
      if (normalizedPath.startsWith(route + '/')) {
        return tier as FeatureTier;
      }
    }
  }

  return null;
}

/**
 * Check if a route is enabled based on current feature flags
 * @param pathname - The route path to check
 * @param flags - Current feature flag state
 * @returns True if the route is accessible (warning or enabled state)
 */
export function isRouteEnabled(
  pathname: string,
  flags: Record<string, FeatureFlagState | boolean>
): boolean {
  const tier = getFeatureTierForRoute(pathname);

  // Not a gated route - always accessible
  if (!tier) {
    return true;
  }

  // Check if the tier's flag allows access (warning or enabled)
  const flagKey = FEATURE_TIERS[tier].flagKey;
  return isFeatureAccessible(flags[flagKey]);
}

/**
 * Check if a route should show warning badges
 * @param pathname - The route path to check
 * @param flags - Current feature flag state
 * @returns True if the route should show warning badges
 */
export function shouldRouteShowWarning(
  pathname: string,
  flags: Record<string, FeatureFlagState | boolean>
): boolean {
  const tier = getFeatureTierForRoute(pathname);

  // Not a gated route - no warning
  if (!tier) {
    return false;
  }

  const flagKey = FEATURE_TIERS[tier].flagKey;
  return shouldShowWarningBadge(flags[flagKey]);
}

/**
 * Get the feature flag key for a route
 * @param pathname - The route path to check
 * @returns The feature flag key or null if not gated
 */
export function getFeatureFlagKeyForRoute(pathname: string): FeatureFlagKey | null {
  const tier = getFeatureTierForRoute(pathname);
  return tier ? FEATURE_TIERS[tier].flagKey : null;
}

/**
 * Get ordered list of feature tiers for UI display
 */
export function getOrderedFeatureTiers(): Array<[FeatureTier, FeatureTierConfig]> {
  return Object.entries(FEATURE_TIERS)
    .sort(([, a], [, b]) => a.order - b.order) as Array<[FeatureTier, FeatureTierConfig]>;
}

/**
 * Check if an in-call feature is enabled based on current feature flags
 * In-call features are components within the call page (e.g., Search tab, Map tab)
 * @param featureName - The in-call feature name (e.g., 'search', 'map')
 * @param flags - Current feature flag state
 * @returns True if the feature is accessible (warning or enabled state)
 */
export function isInCallFeatureEnabled(
  featureName: string,
  flags: Record<string, FeatureFlagState | boolean>
): boolean {
  for (const config of Object.values(FEATURE_TIERS)) {
    if (config.inCallFeatures?.includes(featureName)) {
      return isFeatureAccessible(flags[config.flagKey]);
    }
  }
  // Feature not found in any tier - always enabled
  return true;
}

/**
 * Check if an in-call feature should show warning badges
 * @param featureName - The in-call feature name (e.g., 'search', 'map')
 * @param flags - Current feature flag state
 * @returns True if the feature should show warning badges
 */
export function shouldInCallFeatureShowWarning(
  featureName: string,
  flags: Record<string, FeatureFlagState | boolean>
): boolean {
  for (const config of Object.values(FEATURE_TIERS)) {
    if (config.inCallFeatures?.includes(featureName)) {
      return shouldShowWarningBadge(flags[config.flagKey]);
    }
  }
  // Feature not found in any tier - no warning
  return false;
}

/**
 * Get the feature tier for an in-call feature
 * @param featureName - The in-call feature name (e.g., 'search', 'map')
 * @returns The feature tier or null if not gated
 */
export function getInCallFeatureTier(featureName: string): FeatureTier | null {
  for (const [tier, config] of Object.entries(FEATURE_TIERS)) {
    if (config.inCallFeatures?.includes(featureName)) {
      return tier as FeatureTier;
    }
  }
  return null;
}
